// 統一的訊息路由服務
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import { apiService } from './api'
import { VIP_GATE_KEYS, type VipGateKey } from '@/constants/vipGateKeys'

export interface MessageAccessResult {
  canAccess: boolean
  requiresVip: boolean
  accessKey: VipGateKey
  reason?: string
}

export interface MessageRouteOptions {
  showToast?: boolean
  toastDelay?: number
  fallbackRoute?: string
}

/**
 * 檢查用戶的訊息訪問權限
 */
export async function checkMessageAccess(): Promise<MessageAccessResult> {
  const authStore = useAuthStore()

  try {
    // 檢查是否已登入
    if (!authStore.isAuthenticated) {
      return {
        canAccess: false,
        requiresVip: false,
        accessKey: VIP_GATE_KEYS.messaging,
        reason: 'not_authenticated'
      }
    }

    // 調用後端 API 檢查權限
    const response = await apiService.messages.checkAccess()

    if (response.data.success) {
      return {
        canAccess: response.data.data.can_send_messages,
        requiresVip: response.data.data.requires_vip,
        accessKey: VIP_GATE_KEYS.messaging,
        reason: response.data.data.requires_vip ? 'vip_required' : undefined
      }
    } else {
      return {
        canAccess: false,
        requiresVip: false,
        accessKey: VIP_GATE_KEYS.messaging,
        reason: 'api_error'
      }
    }
  } catch (error) {
    console.error('檢查訊息權限失敗:', error)
    return {
      canAccess: false,
      requiresVip: false,
      accessKey: VIP_GATE_KEYS.messaging,
      reason: 'network_error'
    }
  }
}

/**
 * 統一的訊息路由服務
 * 處理所有進入訊息相關頁面的邏輯
 */
export function useMessageRouter() {
  const router = useRouter()
  useAuthStore()
  const toast = useToast()
  const { t } = useI18n()

  /**
   * 統一的訊息頁面導航
   * @param targetUserId 目標用戶ID（可選，如果提供則直接進入聊天）
   * @param options 路由選項
   */
  const navigateToMessages = async (targetUserId?: string, options: MessageRouteOptions = {}) => {
    const {
      showToast = true,
      toastDelay = 2000,
      fallbackRoute = '/subscription'
    } = options

    console.log('🔄 統一訊息導航:', { targetUserId, options })

    // 檢查訪問權限
    const accessResult = await checkMessageAccess()

    console.log('🔍 訊息權限檢查結果:', accessResult)

    if (accessResult.canAccess) {
      // 有權限，直接導航
      if (targetUserId) {
        console.log('✅ 導航到聊天頁面:', targetUserId)
        router.push(`/chat/${targetUserId}`)
      } else {
        console.log('✅ 導航到訊息列表')
        router.push('/messages')
      }
    } else {
      // 沒有權限，處理不同情況
      handleAccessDenied(accessResult, fallbackRoute, showToast, toastDelay)
    }
  }

  /**
   * 處理訪問被拒絕的情況
   */
  const handleAccessDenied = (
    accessResult: MessageAccessResult,
    fallbackRoute: string,
    showToast: boolean,
    toastDelay: number
  ) => {
    switch (accessResult.reason) {
      case 'not_authenticated':
        console.log('❌ 用戶未登入，跳轉到登入頁面')
        router.push('/auth/login')
        break

      case 'vip_required':
        console.log('💎 需要 VIP 權限，顯示提示並跳轉')
        if (showToast) {
          showVipRequiredToast(toastDelay)
        }
        setTimeout(() => {
          router.push(fallbackRoute)
        }, toastDelay)
        break

      case 'api_error':
      case 'network_error':
      default:
        console.log('⚠️ 系統錯誤，顯示錯誤提示')
        if (showToast) {
          toast.error(t('messages.accessError'), {
            timeout: 3000
          })
        }
        break
    }
  }

  /**
   * 顯示 VIP 需求提示
   */
  const showVipRequiredToast = (delay: number) => {
    toast.info(t('messages.vipRequiredToast'), {
      timeout: delay + 500,
      toastClassName: 'vip-toast',
      bodyClassName: 'vip-toast-body'
    })
  }

  return {
    navigateToMessages,

    /**
     * Header 訊息按鈕點擊處理
     */
    async handleHeaderMessagesClick() {
      console.log('📱 Header 訊息按鈕點擊')
      await navigateToMessages()
    },

    /**
     * 移動端 Header 訊息按鈕點擊處理
     */
    async handleMobileHeaderMessagesClick() {
      console.log('📱 移動端 Header 訊息按鈕點擊')
      await navigateToMessages()
    },

    /**
     * 發現頁面卡片訊息按鈕點擊處理
     */
    async handleDiscoverCardMessageClick(targetUserId: string) {
      console.log('💌 發現頁面卡片訊息按鈕點擊:', targetUserId)
      await navigateToMessages(targetUserId)
    },

    /**
     * 底部導航訊息按鈕點擊處理
     */
    async handleBottomNavMessagesClick() {
      console.log('📱 底部導航訊息按鈕點擊')
      await navigateToMessages()
    },

    /**
     * 用戶詳情頁面訊息按鈕點擊處理
     */
    async handleUserDetailMessageClick(targetUserId: string) {
      console.log('👤 用戶詳情頁面訊息按鈕點擊:', targetUserId)
      await navigateToMessages(targetUserId)
    }
  }
}
